
import { useEffect } from "react";
import { Helmet } from "react-helmet";
import { motion } from "framer-motion";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import BackToTopButton from "../components/BackToTopButton";
import FloatingSocialButtons from "../components/FloatingSocialButtons";
import { Play, Film, Zap, Users, Award, Clock, Layers, PenTool } from "lucide-react";

const AnimationPage = () => {
  useEffect(() => {
    document.title = "2D Animation Services - DG IT Pvt. Ltd.";
    window.scrollTo(0, 0);
  }, []);

  const services = [
    {
      icon: <Play className="w-8 h-8 text-blue-600" />,
      title: "Explainer Videos",
      description: "Engaging animated explainer videos that simplify complex concepts and boost understanding"
    },
    {
      icon: <Film className="w-8 h-8 text-blue-600" />,
      title: "Character Animation",
      description: "Bringing characters to life with smooth, expressive animations for stories and brands"
    },
    {
      icon: <Zap className="w-8 h-8 text-blue-600" />,
      title: "Motion Graphics",
      description: "Dynamic motion graphics for logos, titles, and visual effects that captivate audiences"
    },
    {
      icon: <Layers className="w-8 h-8 text-blue-600" />,
      title: "2D Animation",
      description: "Traditional 2D animation for commercials, educational content, and entertainment"
    },
    {
      icon: <PenTool className="w-8 h-8 text-blue-600" />,
      title: "Whiteboard Animation",
      description: "Hand-drawn style animations perfect for educational and business presentations"
    },
    {
      icon: <Users className="w-8 h-8 text-blue-600" />,
      title: "Social Media Animation",
      description: "Eye-catching animated content optimized for social media platforms and engagement"
    }
  ];

  const animationSoftware = [
    { name: "Adobe After Effects", icon: "🎬" },
    { name: "Adobe Animate", icon: "🎨" },
    { name: "Toon Boom", icon: "📽️" },
    { name: "Blender", icon: "🔧" },
    { name: "Cinema 4D", icon: "🎭" },
    { name: "Lottie", icon: "⚡" }
  ];

  const animationTypes = [
    "Explainer Videos",
    "Logo Animation",
    "Character Animation",
    "Motion Graphics",
    "Whiteboard Animation",
    "Infographic Animation",
    "Product Animation",
    "Educational Videos"
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <meta name="description" content="Professional 2D animation services in Nepal. Explainer videos, character animation, motion graphics, and whiteboard animation." />
        <meta name="keywords" content="2D animation, explainer videos, motion graphics, character animation, whiteboard animation, Nepal" />
        <meta property="og:title" content="2D Animation Services - DG IT Pvt. Ltd." />
      </Helmet>
      
      <Navbar />
      <main className="flex-grow pt-24">
        <div className="container mx-auto px-6 py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <h1 className="text-4xl md:text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-blue-800 mb-4">
              2D Animation Services
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Bring your ideas to life with captivating 2D animations that tell your story and engage your audience.
            </p>
          </motion.div>

          {/* Services Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="glass-card rounded-xl p-6 hover:shadow-xl transition-shadow"
              >
                <div className="mb-4">{service.icon}</div>
                <h3 className="text-xl font-bold mb-2">{service.title}</h3>
                <p className="text-gray-600">{service.description}</p>
              </motion.div>
            ))}
          </div>

          {/* Animation Software Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="bg-gray-50 rounded-2xl p-8 mb-16"
          >
            <h2 className="text-3xl font-bold text-center mb-8">Animation Software We Use</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
              {animationSoftware.map((software, index) => (
                <div key={index} className="bg-white rounded-lg p-4 text-center shadow-md hover:shadow-lg transition-shadow">
                  <div className="text-3xl mb-2">{software.icon}</div>
                  <h3 className="font-semibold">{software.name}</h3>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Animation Types Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="mb-16"
          >
            <h2 className="text-3xl font-bold text-center mb-8">Animation Types We Create</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {animationTypes.map((type, index) => (
                <div key={index} className="flex items-center space-x-3 p-3 bg-blue-50 rounded-lg">
                  <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                  <span className="font-medium">{type}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Animation Process Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            className="mb-16"
          >
            <h2 className="text-3xl font-bold text-center mb-12">Our Animation Process</h2>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
              {[
                { step: "01", title: "Script", desc: "Developing the story and script" },
                { step: "02", title: "Storyboard", desc: "Creating visual storyboards" },
                { step: "03", title: "Design", desc: "Character and asset design" },
                { step: "04", title: "Animation", desc: "Bringing everything to life" },
                { step: "05", title: "Post", desc: "Sound, music, and final editing" }
              ].map((item, index) => (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                    {item.step}
                  </div>
                  <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                  <p className="text-gray-600">{item.desc}</p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-2xl p-10 text-white text-center">
            <h2 className="text-3xl font-bold mb-4">Ready to Animate Your Ideas?</h2>
            <p className="text-xl text-blue-100 mb-6">
              Let's create stunning animations that captivate and engage your audience.
            </p>
            <motion.a
              href="/quote"
              whileHover={{ scale: 1.05 }}
              className="inline-block px-8 py-4 bg-white text-blue-600 font-bold rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
            >
              Get Started Now
            </motion.a>
          </div>
        </div>
      </main>
      <Footer />
      <BackToTopButton />
      <FloatingSocialButtons />
    </div>
  );
};

export default AnimationPage;
